import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useHistory, useParams } from 'react-router';
import { path } from 'ramda';

import { getIsPlanErknmStatusApproved } from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IUploadModalForm, UploadModal } from 'ImportPackage/MainModule/components/UploadModal';
import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { importFileTypesExec } from 'ImportPackage/MainModule/constants/importFileTypesExec';
import { importFileTypesExecErknm } from 'ImportPackage/MainModule/constants/importFileTypesExecErknm';
import { importFileTypesProc } from 'ImportPackage/MainModule/constants/importFileTypesProc';
import { importFileTypesProcErknm } from 'ImportPackage/MainModule/constants/importFileTypesProcErknm';
import {
  getUserAccountTypeIsInternal,
  getUserProfileIsTest,
  getUserRoleSysName,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import {
  knmGetSuccess,
  validateUnsignedKnmByPlanIdClear,
  validateUnsignedKnmByPlanIdPost,
} from 'KnmPackage/MainModule/actions/knmActions';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { IKnmReasonChangeType } from 'APIPackage/CatalogsModule/interfaces/IKnmReasonChangeType';
import { IKnmDocumentRequest } from 'APIPackage/KnmModule/interfaces/IKnmDocumentRequest';
import { importCreateTemplateWithFile } from 'ImportPackage/MainModule/actions/importActions';
import { getAccountOrganizationsWithDistrictData } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { SignModal } from 'CorePackage/MainModule/components/SignModal';
import { importFileTypesTestExecErknm } from 'ImportPackage/MainModule/constants/importFileTypesTestExecErknm';
import { getDictionariesKnoOrganizationsData } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IDictionaryValues } from 'APIPackage/DictionariesModule/interfaces/IDictionaryValues';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { getSubjectInfo } from 'CorePackage/MainModule/common/getSubjectInfo';
import { IValidatedUnsignedKnmError } from 'APIPackage/KnmModule/interfaces/IValidatedUnsignedKnmByPlan';
import { AccessDeniedForTestPlanModal } from 'CorePackage/MainModule/components/AccessDeniedForTestPlanModal';
import { getKnmModalIsVisible } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';

import {
  planCheckIsPrivilegesKno,
  planInitPage,
  planSignPlanById,
  planSignTestPlanById,
  planUpdate,
} from '../actions/planActions';
import { getPlanData, getPlanDataErknmStatus, getPlanFormRef } from '../selectors/planSelectors';
import styles from './Plan.module.scss';
import { PlanHeader } from './PlanHeader';
import { PlanSideInfo } from './PlanSideInfo';
import { PlanSignInfoModal } from './PlanSignInfoModal';
import { PlanSignUploadModal } from './PlanSignUploadModal';
import { PlanAddModal } from './PlanAddModal';
import { PlanChangeKnoModal } from './PlanChangeKnoModal';
import { createNewKnmFromPlan } from '../common/createNewKnmFromPlan';
import { adminGetFetchSettings } from '../../../PersonalAccountPackage/AdminModule/actions/adminAction';
import { getCatalogKnmTypes248SelectOptionsSelector } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { ValidatedUnsignedKnmByPlan } from './ValidatedUnsignedKnmByPlan';
import { IPlanFormRef } from '../interfaces/IPlanFormRef';
import { IPlanFormValues } from '../interfaces/PlanForm';
import { buildPlanUpdateRequest } from '../common/buildPlanUpdateRequest';
import { PlanBody } from './PlanBody';

interface IPlanProps {
  isErknm: boolean;
  roleSysName: UserRoleEnum | undefined;
  plan: IKnmPlan | undefined;
  planErknmStatus: ErknmPlanStatusesEnum | undefined;
  userProsecutorOrganisations: IUserOrganisation[];
  knmTypes: IKnmTypeSelectOption[];
  isInternalAccount: boolean;
  knoOrganizations: IDictionaryValues<IKnoOrganizationDictionary> | undefined;
  isUserTest: boolean;
  isAccessDeniedForTestPlanModalVisible: boolean;
  formRef: IPlanFormRef | undefined;
  settings: IAdminSetting[];
}

const PlanWithoutStore = ({
  isErknm,
  roleSysName,
  plan,
  planErknmStatus,
  userProsecutorOrganisations,
  knmTypes,
  isInternalAccount,
  knoOrganizations,
  isUserTest,
  isAccessDeniedForTestPlanModalVisible,
  formRef,
  settings,
}: IPlanProps) => {
  const isPlanErknmStatusApproved: boolean = getIsPlanErknmStatusApproved(planErknmStatus);
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const setInitialKnmData: (initialData: IKnm | undefined) => void = useCallback(
    (initialData: IKnm | undefined) => initialData && dispatch(knmGetSuccess({ ...initialData, initial: true })),
    [dispatch],
  );

  const initPlanPage: (id: string) => void = useCallback((id: string) => dispatch(planInitPage(id)), [dispatch]);

  const updatePlan: (
    id: string,
    plan: Partial<IKnmPlan> & {
      ervkProsecutorOrgUuid: string;
      ervkProsecutorOrgVersionUuid: string;
      prosecutorOrgName: string;
    },
    cb: () => void,
  ) => void = useCallback(
    (
      id: string,
      planData: Partial<IKnmPlan> & {
        ervkProsecutorOrgUuid: string;
        ervkProsecutorOrgVersionUuid: string;
        prosecutorOrgName: string;
      },
      cb: () => void,
    ) => dispatch(planUpdate(id, planData, cb)),
    [dispatch],
  );

  const { id: planId } = useParams<{ id: string }>();
  const [isSignModalVisible, setIsSignModalVisible] = useState(false);
  const [isSignInfoModalVisible, setIsSignInfoModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isAddKnmModalVisible, setIsAddKnmModalVisible] = useState(false);
  const [isChangeKnoModalVisible, setIsChangeKnoModalVisible] = useState(false);
  const [isSignUploadModalVisible, setIsSignUploadModalVisible] = useState(false);
  const history = useHistory();
  const userProsecutorOrganisationsSelectOptions:
    | IUserOrganisationSelectOption[]
    | undefined = userProsecutorOrganisations?.map(item => ({
    ...item,
    label: `${item.districtName ? `${item.districtName}, ` : ''}${item.fullName}`,
    value: item.id,
    districtName: item.districtName,
    districtId: item.districtId,
  }));

  const isFioAndJobTitle414 = isLaterThanAdminSetting(
    plan?.created,
    settings,
    AdminSettingCodesEnum.PLAN_SIGNER_166_4_1_4_START_FROM,
  );

  const isEnableKnoValidationSigning = useMemo(() => {
    const option = settings?.find(setting => setting.code === AdminSettingCodesEnum.ENABLED_KNO_VALIDATION_SIGNING);

    return option?.value === 'true';
  }, [settings]);

  useEffect(() => {
    initPlanPage(planId);
  }, [planId, initPlanPage]);

  useEffect(() => {
    const currentOgrn = knoOrganizations?.values.find(item => item.value.id === plan?.knoId)?.value.ogrn;

    if (currentOgrn) {
      dispatch(planCheckIsPrivilegesKno(currentOgrn));
    }
  }, [dispatch, knoOrganizations, plan]);

  const handleCloseSignModal = useCallback(() => {
    dispatch(validateUnsignedKnmByPlanIdClear());
    setIsSignModalVisible(false);
  }, [dispatch]);

  const getPlanFormValues = useCallback(
    (): IPlanFormValues => ({
      signerFullName: formRef?.current.values.signerFullName,
      signerPosition: formRef?.current.values.signerPosition?.value,
    }),
    [formRef],
  );

  const handleSignPlan = useCallback(
    (certificate: string, errors: IValidatedUnsignedKnmError[] = []) => {
      if (planId) {
        dispatch(planSignPlanById(+planId, certificate, errors, getPlanFormValues()));
      }
    },
    [dispatch, planId, getPlanFormValues],
  );

  const handleOpenSignModalOrTestSign = useCallback(() => {
    if (!isUserTest) {
      if (!isFioAndJobTitle414) {
        setIsSignModalVisible(true);
      } else {
        const isValidationErrors = formRef?.current.form.getState()?.hasValidationErrors;

        if (isValidationErrors) {
          formRef?.current.form.submit();
        } else if (plan) {
          updatePlan(planId, buildPlanUpdateRequest(plan, getPlanFormValues()), () => {
            setIsSignModalVisible(true);
          });
        }
      }
    } else if (planId) {
      if (!isFioAndJobTitle414) {
        dispatch(planSignTestPlanById(+planId));
      } else {
        const isValidationErrors = formRef?.current.form.getState()?.hasValidationErrors;

        if (isValidationErrors) {
          formRef?.current.form.submit();
        } else if (plan) {
          updatePlan(planId, buildPlanUpdateRequest(plan, getPlanFormValues()), () => {
            dispatch(planSignTestPlanById(+planId));
          });
        }
      }
    }
  }, [isUserTest, planId, formRef, plan, isFioAndJobTitle414, updatePlan, dispatch, getPlanFormValues]);

  const handleCloseSignInfoModal = useCallback(() => {
    setIsSignInfoModalVisible(false);
  }, []);

  const handleOpenSignInfoModal = useCallback(() => {
    setIsSignInfoModalVisible(true);
  }, []);

  const createNewKnm = useCallback(
    (selectedReason?: IKnmReasonChangeType, documents?: IKnmDocumentRequest) => {
      createNewKnmFromPlan({
        history,
        knmTypes,
        plan,
        setInitialKnmData,
        userProsecutorOrganisationsSelectOptions,
        documents,
        selectedReason,
      });
    },
    [history, knmTypes, plan, setInitialKnmData, userProsecutorOrganisationsSelectOptions],
  );

  const handleAddKnmButtonClick = useCallback(() => {
    const currentDate = new Date();

    const currentYear = currentDate.getFullYear();
    const nextYear = currentDate.getFullYear() + 1;

    /**
     * В результате обсуждений TASK-5292 аналитиками установлено,
     * что указывать changePlanReason необходимо для Утвержденного плана
     * текущего и СЛЕДУЮЩЕГО года.
     */
    if (isPlanErknmStatusApproved && (plan?.year === currentYear || plan?.year === nextYear)) {
      setIsAddKnmModalVisible(true);
    } else {
      createNewKnm();
    }
  }, [createNewKnm, isPlanErknmStatusApproved, plan]);

  const handleApplyModalToAddKnm = useCallback(
    (selectedReason: IKnmReasonChangeType, documents: IKnmDocumentRequest) => createNewKnm(selectedReason, documents),
    [createNewKnm],
  );

  const closeUploadModal = useCallback(() => setIsUploadModalVisible(false), []);
  const handleUploadButtonClick = useCallback(() => setIsUploadModalVisible(true), []);

  const handleOpenSignUploadModal = useCallback(() => setIsSignUploadModalVisible(true), []);
  const closeSignUploadModal = useCallback(() => setIsSignUploadModalVisible(false), []);

  const getFileTypes = () => {
    switch (roleSysName) {
      case UserRoleEnum.SUPERIOR_PROSECUTOR:
      case UserRoleEnum.PROSECUTOR:
        return isErknm ? importFileTypesProcErknm : importFileTypesProc;
      case UserRoleEnum.SUPERVISOR:
        return isErknm
          ? (isInternalAccount && importFileTypesTestExecErknm) || importFileTypesExecErknm
          : importFileTypesExec;
      default:
        return [];
    }
  };

  const handleUploadModalApply = useCallback(
    (values: IUploadModalForm) => {
      dispatch(importCreateTemplateWithFile(values, values.planId || undefined));
    },
    [dispatch],
  );

  const handleAddKnmModalClose = useCallback(() => setIsAddKnmModalVisible(false), []);
  const handleChangeKnoModalClose = useCallback(() => setIsChangeKnoModalVisible(false), []);

  const handleOpenChangeKnoModal = useCallback(() => setIsChangeKnoModalVisible(true), []);

  useEffect(() => {
    dispatch(adminGetFetchSettings());
  }, [dispatch]);

  /**
   * Валидация паспортов внутри плана на возможность подписания тем
   * же сертификатом, что и будет использован для плана
   */
  const handleValidationKnmsInPlan = useCallback(
    async (certificate: string) => {
      if (planId) {
        const subjectName = (await path(['SubjectName'], certificate)) as string;
        const subjectInfo = getSubjectInfo(subjectName);

        dispatch(
          validateUnsignedKnmByPlanIdPost(
            Number(planId),
            subjectInfo.fioSigner || '',
            subjectInfo.fioSignerCN || '',
            subjectInfo.org || '',
          ),
        );
      }
    },
    [dispatch, handleCloseSignModal],
  );

  return (
    <>
      <div className={styles.MainContent}>
        <PlanHeader
          handleOpenSignModal={handleOpenSignModalOrTestSign}
          handleUploadButtonClick={handleUploadButtonClick}
          handleOpenSignInfoModal={handleOpenSignInfoModal}
          handleAddKnmButtonClick={handleAddKnmButtonClick}
          handleOpenSignUploadModal={handleOpenSignUploadModal}
          handleOpenChangeKnoModal={handleOpenChangeKnoModal}
        />
        <div className={styles.Body}>
          <PlanBody handleAddKnmButtonClick={handleAddKnmButtonClick} />
          <PlanSideInfo />
        </div>
      </div>
      <SignModal
        visible={isSignModalVisible}
        title="Подписание плана"
        onClose={handleCloseSignModal}
        onSign={certificate => handleSignPlan(certificate)}
        onValidation={handleValidationKnmsInPlan}
        renderValidationResultComponent={certificate => (
          <ValidatedUnsignedKnmByPlan
            handleCloseSignModal={handleCloseSignModal}
            handleSignPlan={(errors: IValidatedUnsignedKnmError[]) => handleSignPlan(certificate, errors)}
            isFIOandJobTitle_4_1_4={isFioAndJobTitle414}
            isEnableKnoValidationSigning={isEnableKnoValidationSigning}
          />
        )}
      />
      <AccessDeniedForTestPlanModal visible={isAccessDeniedForTestPlanModalVisible} />
      <PlanSignInfoModal visible={isSignInfoModalVisible} onClose={handleCloseSignInfoModal} />
      <UploadModal
        visible={isUploadModalVisible}
        fileTypes={getFileTypes()}
        onApply={handleUploadModalApply}
        onClose={closeUploadModal}
        isAdjustment
      />
      <PlanSignUploadModal visible={isSignUploadModalVisible} onClose={closeSignUploadModal} />
      <PlanAddModal
        visible={isAddKnmModalVisible}
        isErknm={isErknm}
        onClose={handleAddKnmModalClose}
        planId={+planId}
        onApply={handleApplyModalToAddKnm}
        isKnm
      />
      <PlanChangeKnoModal isVisible={isChangeKnoModalVisible} onClose={handleChangeKnoModalClose} />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  roleSysName: getUserRoleSysName(state),
  plan: getPlanData(state),
  planErknmStatus: getPlanDataErknmStatus(state),
  userProsecutorOrganisations: getAccountOrganizationsWithDistrictData(state).prosecutor,
  knmTypes: getCatalogKnmTypes248SelectOptionsSelector(state)({}),
  isInternalAccount: getUserAccountTypeIsInternal(state),
  knoOrganizations: getDictionariesKnoOrganizationsData(state),
  isUserTest: getUserProfileIsTest(state),
  isAccessDeniedForTestPlanModalVisible: getKnmModalIsVisible([KnmModalsEnum.ACCESS_DENIED_PLAN_FOR_TEST])(state),
  formRef: getPlanFormRef(state),
  settings: getAdminFetchSettings(state),
});

export const Plan = connect(mapStateToProps)(PlanWithoutStore);
